
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ErrorRecoveryManager } from "@/utils/errorRecovery/ErrorRecoveryManager";
import { TaskPrioritizationManager } from "@/utils/taskManagement/TaskPrioritizationManager";
import { ResourceOptimizationManager } from "@/utils/resourceManagement/ResourceOptimizationManager";

export const WorkflowControl = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();
  const WORKFLOW_CONTROL_ID = '67e2e098-d495-4c63-a656-4ebb2d46e2df';
  const [errorRecovery] = useState(() => new ErrorRecoveryManager(WORKFLOW_CONTROL_ID));
  const [taskManager] = useState(() => new TaskPrioritizationManager());
  const [resourceManager] = useState(() => new ResourceOptimizationManager(WORKFLOW_CONTROL_ID));

  useEffect(() => {
    checkWorkflowState();
    setupWorkflowSubscription();
    startResourceMonitoring();
  }, []);

  const checkWorkflowState = async () => {
    try {
      const { data } = await supabase
        .from('workflow_state')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      setIsRunning(data?.is_running || false);
    } catch (error) {
      console.error('Error checking workflow state:', error);
    }
  };

  const setupWorkflowSubscription = () => {
    const channel = supabase
      .channel('workflow-state')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workflow_state'
        },
        (payload: any) => {
          console.log('Workflow state changed:', payload);
          if (payload.new) {
            setIsRunning(payload.new.is_running);
            if (!payload.new.is_running) {
              setProgress(0);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const startResourceMonitoring = () => {
    const interval = setInterval(async () => {
      if (isRunning) {
        const metrics = {
          cpuUsage: Math.random() * 100,
          memoryUsage: Math.random() * 100,
          taskCount: Math.floor(Math.random() * 10)
        };
        
        await resourceManager.trackResourceUsage(metrics);
        await resourceManager.optimizeResourceAllocation();
      }
    }, 30000);

    return () => clearInterval(interval);
  };

  const startWorkflow = async () => {
    try {
      await errorRecovery.executeWithRecovery(
        async () => {
          const { error } = await supabase
            .from('workflow_state')
            .insert([{ is_running: true }]);

          if (error) throw error;

          toast({
            title: "Workflow Started",
            description: "The autonomous workflow has been activated."
          });

          setProgress(0);
          simulateProgress();
        },
        { 
          agentId: WORKFLOW_CONTROL_ID,
          operationType: 'workflow_start'
        }
      );
    } catch (error) {
      console.error('Error starting workflow:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to start workflow. Please try again."
      });
    }
  };

  const stopWorkflow = async () => {
    try {
      await errorRecovery.executeWithRecovery(
        async () => {
          const { error: stateError } = await supabase
            .from('workflow_state')
            .insert([{ is_running: false }]);

          if (stateError) throw stateError;

          const { error: functionError } = await supabase
            .rpc('cleanup_workflow_tasks');

          if (functionError) throw functionError;

          toast({
            title: "Workflow Stopped",
            description: "The autonomous workflow has been deactivated."
          });

          setProgress(0);
        },
        {
          agentId: WORKFLOW_CONTROL_ID,
          operationType: 'workflow_stop'
        }
      );
    } catch (error) {
      console.error('Error stopping workflow:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to stop workflow. Please try again."
      });
    }
  };

  const simulateProgress = () => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prevProgress + 1;
      });
    }, 100);
  };

  const handleToggle = async (checked: boolean) => {
    if (checked) {
      await startWorkflow();
    } else {
      await stopWorkflow();
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="font-medium leading-none">
              Workflow Control
            </h3>
            <p className="text-sm text-muted-foreground">
              Workflow Status: {isRunning ? 'Running' : 'Paused'}
              {isRunning && progress > 0 && ` (${progress}%)`}
            </p>
          </div>
          <Switch
            checked={isRunning}
            onCheckedChange={handleToggle}
            aria-label="Toggle workflow"
          />
        </div>
        {isRunning && (
          <div className="mt-4">
            <Progress value={progress} className="w-full" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
